import { InformationCircleIcon } from "@heroicons/react/24/outline";
import {
  Chart as ChartJS,
  Filler,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
  type ChartOptions,
  type ChartType,
  type Plugin,
  type TooltipModel,
} from "chart.js";
import { useEffect, useRef, useState } from "react";
import { Line } from "react-chartjs-2";
import { VideoInsightScene, VideoInsightSceneScores } from "@/api/studio/insights/types/attention.type";
import { InfoModalTrigger } from "@/components/ui/modal";
import { VideoScenePillarTab } from "@/features/reports/components/in-depth/video-scene-pillar-tab";
import { getTimeTicks } from "@/features/reports/utils/get-time-ticks";
import { VIDEO_GRAPH_EXPLAINED_MODAL_CONFIG, VideoGraphExplainedModal } from "@/features/reports/modals/video-graph-explained-modal";
import { asColor, asPlaybackTime } from "@/utils/format";
import { cn } from "@/utils/classes";

const SELECTED_SCENE_HIGHLIGHT_PLUGIN_ID = "selectedSceneHighlight";

interface SelectedSceneHighlightPluginOptions {
  startTime: number;
  endTime: number;
}

declare module "chart.js" {
  interface PluginOptionsByType<TType extends ChartType> {
    [SELECTED_SCENE_HIGHLIGHT_PLUGIN_ID]?: TType extends "line" ? SelectedSceneHighlightPluginOptions : never;
  }
}

// Shades the selected scene's time range and marks its boundaries with dashed lines on the
// scene performance chart, so the graph stays visually in sync with the highlighted marker/strip below.
const selectedSceneHighlightPlugin: Plugin<"line", SelectedSceneHighlightPluginOptions> = {
  id: SELECTED_SCENE_HIGHLIGHT_PLUGIN_ID,
  beforeDatasetsDraw: (chart, _args, options) => {
    if (!options) return;
    const { ctx, chartArea, scales } = chart;
    const xScale = scales.x;
    if (!xScale) return;

    const left = xScale.getPixelForValue(options.startTime);
    const right = xScale.getPixelForValue(options.endTime);

    ctx.save();
    ctx.fillStyle = "rgba(255,255,255,0.12)";
    ctx.fillRect(left, chartArea.top, right - left, chartArea.bottom - chartArea.top);
    ctx.restore();
  },
  afterDatasetsDraw: (chart, _args, options) => {
    if (!options) return;
    const { ctx, chartArea, scales } = chart;
    const xScale = scales.x;
    if (!xScale) return;

    const left = xScale.getPixelForValue(options.startTime);
    const right = xScale.getPixelForValue(options.endTime);

    ctx.save();
    ctx.strokeStyle = "rgba(255,255,255,0.7)";
    ctx.lineWidth = 1.5;
    ctx.setLineDash([4, 3]);
    ctx.beginPath();
    ctx.moveTo(left, chartArea.top);
    ctx.lineTo(left, chartArea.bottom);
    ctx.moveTo(right, chartArea.top);
    ctx.lineTo(right, chartArea.bottom);
    ctx.stroke();
    ctx.restore();
  },
};

ChartJS.register(LinearScale, LineController, PointElement, LineElement, Filler, Tooltip, selectedSceneHighlightPlugin);

// Payoff, Authenticity and Message memory are video-level only - they can't be reliably assigned
// to scenes, so they're never plotted here.
type QualityScoreKey = keyof Pick<VideoInsightSceneScores, "hook" | "visualEase" | "hold" | "intensity" | "brandAttribution">;

interface QualityScoreConfig {
  key: QualityScoreKey;
  label: string;
  sublabel?: string;
  color: string;
  // Shown in the tooltip when a scene has no score for it
  nullLabel?: string;
}

interface PillarConfig {
  key: "attention" | "emotion" | "memory";
  label: string;
  color: string;
  qualityScores: QualityScoreConfig[];
}

const PILLARS: PillarConfig[] = [
  {
    key: "attention",
    label: "Attention",
    color: "#19D3C8",
    qualityScores: [
      // Hook is only scored in the opening scenes and Hold only after them, so a missing score
      // means the window has already passed or hasn't started yet
      { key: "hook", label: "Hook", sublabel: "first 3s", color: "#A7F3D0", nullLabel: "Earlier" },
      { key: "visualEase", label: "Visual ease", color: "#38BDF8" },
      { key: "hold", label: "Hold", sublabel: "after 3s", color: "#10B981", nullLabel: "Later" },
    ],
  },
  { key: "emotion", label: "Emotion", color: "#F2318A", qualityScores: [{ key: "intensity", label: "Emotion intensity", color: "#F2318A" }] },
  { key: "memory", label: "Memory", color: "#8B5CF6", qualityScores: [{ key: "brandAttribution", label: "Brand attribution", color: "#8B5CF6" }] },
];

const QUALITY_SCORES = PILLARS.flatMap((pillar) => pillar.qualityScores);

const DEFAULT_QUALITY_SCORES: QualityScoreKey[] = ["hook", "hold"];

// Locked to the same width as the chart's y-axis label gutter (forced via the y scale's
// afterFit below), so the scene marker row and time ruler line up with the plotted points.
const Y_AXIS_WIDTH = 30;

// How long a newly toggled-on metric's colour fades in for.
const FADE_IN_DURATION = 400;

interface AnimationDatasetContext {
  datasetIndex: number;
}

const TOOLTIP_ID = "video-scene-performance-graph-tooltip";

/*
 * getOrCreateTooltipEl function
 * Returns the floating HTML tooltip element for a chart, creating and appending it to the
 * canvas's parent the first time it's needed. Chart.js's own tooltip only draws to canvas, so a
 * custom "external" tooltip needs its own DOM element to render richer HTML content into.
 */
const getOrCreateTooltipEl = (chart: ChartJS): HTMLDivElement => {
  const parent = chart.canvas.parentNode as HTMLElement;
  let tooltipEl = parent.querySelector<HTMLDivElement>(`#${TOOLTIP_ID}`);

  if (!tooltipEl) {
    tooltipEl = document.createElement("div");
    tooltipEl.id = TOOLTIP_ID;
    tooltipEl.style.position = "absolute";
    tooltipEl.style.pointerEvents = "none";
    tooltipEl.style.transition = "opacity 0.1s ease";
    tooltipEl.style.transform = "translate(-50%, -100%)";
    tooltipEl.style.paddingBottom = "12px";
    tooltipEl.style.zIndex = "10";
    parent.appendChild(tooltipEl);
  }

  return tooltipEl;
};

// Muted grey (with a bit of opacity) for a quality score's dot when this scene has no score for it.
const MUTED_DOT_COLOR = "#9ca3af";

const TOOLTIP_DIVIDER_HTML = `<div style="height:1px;flex-shrink:0;background:rgba(255,255,255,0.08);"></div>`;

/*
 * renderTooltipRow function
 * Renders one quality score row of the hover tooltip: its label, a dot coloured by score (using
 * the same asColor score-to-colour mapping as the side panel) and its percentage, or a muted dot
 * and nullLabel when the scene has no score for it.
 */
const renderTooltipRow = (label: string, score: number | null, nullLabel = "-"): string => {
  const hasScore = typeof score === "number";
  const dotStyle = hasScore ? `background:${asColor(score)}` : `background:${MUTED_DOT_COLOR};opacity:0.5`;

  return `
    <div style="display:flex;align-items:center;gap:10px;padding:7px 10px;">
      <span style="flex:1;font-size:13px;line-height:18px;font-weight:500;color:#e9e9ec;">${label}</span>
      <span style="width:8px;height:8px;border-radius:9999px;display:inline-block;${dotStyle}"></span>
      <span style="min-width:36px;text-align:right;font-size:13px;line-height:18px;font-weight:600;color:${hasScore ? "#e9e9ec" : "rgba(204,204,204,0.5)"};">${hasScore ? `${Math.round(score)}%` : nullLabel}</span>
    </div>
  `;
};

/*
 * renderTooltipHtml function
 * Renders the hover tooltip for one scene: its label, then each pillar with a selected quality
 * score as a small heading followed by a row per selected quality score, with dividers between.
 */
const renderTooltipHtml = (sceneLabel: string, scores: VideoInsightSceneScores, selectedQualityScores: Set<QualityScoreKey>): string => {
  const pillarSections = PILLARS.map((pillar) => {
    const selected = pillar.qualityScores.filter((qualityScore) => selectedQualityScores.has(qualityScore.key));
    if (selected.length === 0) return null;

    return `
      <p style="font-size:11px;font-weight:500;color:#9ca3b0;margin:0;padding:4px 0 0 10px;">${pillar.label}</p>
      ${selected.map((qualityScore) => renderTooltipRow(qualityScore.label, scores[qualityScore.key], qualityScore.nullLabel)).join("")}
    `;
  }).filter((section) => section !== null);

  return `
    <div style="background:#1d232a;border:1px solid #3a4350;border-radius:10px;padding:12px;width:290px;display:flex;flex-direction:column;gap:4px;box-shadow:0 8px 24px rgba(0,0,0,0.35);">
      <p style="font-size:13px;line-height:18px;font-weight:600;color:#9ca3af;margin:0;">${sceneLabel}</p>
      ${TOOLTIP_DIVIDER_HTML}
      ${pillarSections.join(TOOLTIP_DIVIDER_HTML)}
    </div>
  `;
};

/**
 * getScenePoints function
 * Returns one chart point per scene (at the scene's midpoint) for a quality score, plus a flat
 * synthetic point at the video's start and end so the line doesn't stop short in the middle of
 * the first and last scenes. Scenes without a score for it are null.
 *
 * @param scenes - The video's scenes, in order
 * @param key - The quality score to plot
 * @param totalDuration - The video's duration in seconds
 * @returns The chart points, including the leading and trailing synthetic points
 */
const getScenePoints = (scenes: VideoInsightScene[], key: QualityScoreKey, totalDuration: number) => {
  const scenePoints = scenes.map((scene) => ({ x: scene.startTime + scene.duration / 2, y: scene.scores[key] }));
  return [{ x: 0, y: scenePoints[0].y }, ...scenePoints, { x: totalDuration, y: scenePoints[scenePoints.length - 1].y }];
};

/* Prop Types */
interface VideoScenePerformanceGraphProps {
  scenes: VideoInsightScene[];
  selectedSceneKey: string | null;
  onSelectScene: (sceneKey: string) => void;
}

/**
 * VideoScenePerformanceGraph component
 *
 * Plots scene-level quality scores over the video's duration as an area chart, one point per scene.
 * Attention / Emotion / Memory tabs each open a dropdown to toggle their quality scores, and any mix
 * of pillars can be overlaid. Below the chart, numbered scene markers and a time ruler give the
 * x-axis context; clicking a marker selects that scene, matching VideoScenesStrip.
 */
export const VideoScenePerformanceGraph = ({ scenes, selectedSceneKey, onSelectScene }: VideoScenePerformanceGraphProps) => {
  /* State */
  const [activeQualityScores, setActiveQualityScores] = useState<Set<QualityScoreKey>>(new Set(DEFAULT_QUALITY_SCORES));
  // Tracks which quality scores were already active as of the last render, so only one that's
  // newly switched on this render fades in — otherwise every already-visible line replays the
  // fade whenever another is toggled, since react-chartjs-2 passes a new datasets array.
  const previouslyActiveQualityScores = useRef<Set<QualityScoreKey>>(new Set());

  /* Handlers */
  const toggleQualityScore = (key: QualityScoreKey) => {
    setActiveQualityScores((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  useEffect(() => {
    previouslyActiveQualityScores.current = activeQualityScores;
  }, [activeQualityScores]);

  if (scenes.length === 0) return null;

  /* Consts */
  const totalDuration = scenes.reduce((sum, scene) => sum + scene.duration, 0);
  const timeTicks = getTimeTicks(totalDuration);
  const activeQualityScoreConfigs = QUALITY_SCORES.filter((qualityScore) => activeQualityScores.has(qualityScore.key));
  const selectedScene = scenes.find((scene) => scene.scene === selectedSceneKey);

  // A dataset only plays its draw-in animation the render it's newly switched on — otherwise every
  // already-visible line would redraw itself whenever another quality score is toggled, since
  // react-chartjs-2 passes a brand new datasets array on every render.
  const isNewlyActiveDataset = (ctx: AnimationDatasetContext) => {
    const qualityScore = activeQualityScoreConfigs[ctx.datasetIndex];
    return !!qualityScore && !previouslyActiveQualityScores.current.has(qualityScore.key);
  };

  // Renders a custom HTML tooltip (a row per active quality score) positioned over the hovered point, since Chart.js's built-in tooltip only draws to canvas.
  const handleTooltip = (context: { chart: ChartJS; tooltip: TooltipModel<"line"> }) => {
    const { chart, tooltip } = context;
    const tooltipEl = getOrCreateTooltipEl(chart);

    if (tooltip.opacity === 0) {
      tooltipEl.style.opacity = "0";
      return;
    }

    // Every dataset's data array is [synthetic leading point, ...one per scene, synthetic
    // trailing point], so real scenes sit at indices 1..scenes.length inclusive.
    const dataIndex = tooltip.dataPoints?.[0]?.dataIndex ?? 0;
    const sceneIndex = dataIndex - 1;
    const scene = scenes[sceneIndex];
    if (!scene) {
      tooltipEl.style.opacity = "0";
      return;
    }

    tooltipEl.innerHTML = renderTooltipHtml(`Scene ${sceneIndex + 1}`, scene.scores, activeQualityScores);

    // caretX/caretY are already canvas-local pixel coordinates, and the tooltip is appended into
    // the canvas's own (position: relative) parent, so no viewport/scroll offset math is needed.
    tooltipEl.style.opacity = "1";
    tooltipEl.style.left = `${tooltip.caretX}px`;
    tooltipEl.style.top = `${tooltip.caretY}px`;
  };

  const chartData = {
    datasets: activeQualityScoreConfigs.map((qualityScore) => {
      const data = getScenePoints(scenes, qualityScore.key, totalDuration);

      // Hook only covers the opening scenes and Hold the rest, so Hold is bridged back to the last
      // Hook point to read as one continuous line when both are shown.
      if (qualityScore.key === "hold" && activeQualityScores.has("hook")) {
        const hookPoints = getScenePoints(scenes, "hook", totalDuration);
        const bridgeIndex = data.findIndex((point) => point.y !== null) - 1;
        if (bridgeIndex >= 0 && hookPoints[bridgeIndex].y !== null) data[bridgeIndex] = hookPoints[bridgeIndex];
      }

      return {
        label: qualityScore.label,
        data,
        borderColor: qualityScore.color,
        borderWidth: qualityScore.key === "visualEase" ? 1.5 : 2,
        backgroundColor: (context: { chart: ChartJS }) => {
          const { ctx, chartArea } = context.chart;
          // chartArea isn't known until the first layout pass
          if (!chartArea) return `${qualityScore.color}26`;
          const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
          gradient.addColorStop(0, `${qualityScore.color}80`);
          gradient.addColorStop(1, `${qualityScore.color}1A`);
          return gradient;
        },
        fill: true,
        tension: 0.4,
        spanGaps: true,
        // No point marker on the synthetic leading/trailing points, only on real scene points.
        pointRadius: data.map((_, index) => (index === 0 || index === data.length - 1 ? 0 : 3.5)),
        pointBackgroundColor: qualityScore.color,
        pointBorderColor: "#242c35",
        pointBorderWidth: 2,
      };
    }),
  };

  const chartOptions: ChartOptions<"line"> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index",
      intersect: false,
    },
    animations: {
      // Points/lines appear at their real position immediately; only their colour fades in, so
      // toggling a quality score on doesn't grow the line up from the x-axis or draw itself in.
      // The gradient fill isn't animated, as Chart.js can't interpolate a CanvasGradient.
      x: { duration: 0 },
      y: { duration: 0 },
      colors: {
        type: "color",
        properties: ["borderColor", "pointBackgroundColor", "pointBorderColor"],
        from: "transparent",
        easing: "easeOutQuad",
        duration: (ctx) => (isNewlyActiveDataset(ctx as unknown as AnimationDatasetContext) ? FADE_IN_DURATION : 0),
      },
    },
    scales: {
      x: {
        type: "linear",
        min: 0,
        max: totalDuration,
        display: false,
      },
      y: {
        min: 0,
        max: 100,
        afterFit: (scale) => {
          scale.width = Y_AXIS_WIDTH;
        },
        grid: { color: "rgba(255,255,255,0.08)" },
        ticks: {
          stepSize: 20,
          color: "rgba(233,233,236,0.5)",
          font: { size: 8 },
          callback: (value) => `${value}%`,
        },
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: false,
        external: handleTooltip,
      },
      [SELECTED_SCENE_HIGHLIGHT_PLUGIN_ID]: selectedScene ? { startTime: selectedScene.startTime, endTime: selectedScene.startTime + selectedScene.duration } : undefined,
    },
  };

  /* Render */
  return (
    <div className="flex flex-col gap-4 bg-[#242c35] rounded-lg p-4 min-w-0">
      <div className="flex items-center justify-between">
        <h2 className="text-white font-semibold">Scene performance</h2>
        <InfoModalTrigger modalContent={<VideoGraphExplainedModal />} config={VIDEO_GRAPH_EXPLAINED_MODAL_CONFIG} className="opacity-70 hover:opacity-100">
          <span className="flex items-center gap-1.5">
            <span className="text-white text-xs font-semibold">About this graph</span>
            <InformationCircleIcon className="size-3.5 text-white" />
          </span>
        </InfoModalTrigger>
      </div>

      <div className="flex gap-2">
        {PILLARS.map((pillar) => (
          <VideoScenePillarTab
            key={pillar.key}
            label={pillar.label}
            color={pillar.color}
            options={pillar.qualityScores}
            selectedKeys={activeQualityScores}
            onToggle={toggleQualityScore}
          />
        ))}
      </div>

      <div className="min-w-0" style={{ height: 149, position: "relative" }}>
        <Line data={chartData} options={chartOptions} />
        {activeQualityScoreConfigs.length === 0 && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-center pointer-events-none" style={{ paddingLeft: Y_AXIS_WIDTH }}>
            <p className="text-sm font-semibold text-white">No lines shown</p>
            <p className="text-xs text-white/60">Turn on Attention, Emotion or Memory to see scene scores</p>
          </div>
        )}
      </div>

      {/* Scene markers */}
      <div className="flex w-full" style={{ paddingLeft: Y_AXIS_WIDTH }}>
        {scenes.map((scene, index) => {
          const isSelected = scene.scene === selectedSceneKey;
          return (
            <button
              key={scene.scene}
              type="button"
              onClick={() => onSelectScene(scene.scene)}
              aria-pressed={isSelected}
              className={cn(
                "text-white text-[10px] font-medium text-center py-1 cursor-pointer",
                index !== scenes.length - 1 && "border-r border-[#242c35]",
                isSelected ? "bg-white/30 ring-2 ring-white ring-inset" : "bg-[#323c49]",
              )}
              style={{ width: `${(scene.duration / totalDuration) * 100}%` }}
            >
              {index + 1}
            </button>
          );
        })}
      </div>

      {/* Time ruler */}
      <div className="relative h-4 w-full" style={{ marginLeft: Y_AXIS_WIDTH, width: `calc(100% - ${Y_AXIS_WIDTH}px)` }}>
        {timeTicks.map((tick) => (
          <span key={tick} className="absolute -translate-x-1/2 text-[8px] text-white/50" style={{ left: `${(tick / totalDuration) * 100}%` }}>
            {asPlaybackTime(tick)}
          </span>
        ))}
      </div>
    </div>
  );
};
